import React from "react";

import type { SortOrder } from "@/components/results/sort-utils";
import { colors } from "@/styles/design-tokens";

interface SortOption {
  value: SortOrder;
  label: string;
}

export interface SortDropdownContentProps {
  activeValue: SortOrder;
  options: readonly SortOption[];
  onSelect: (value: SortOrder) => void;
  style?: React.CSSProperties;
  className?: string;
  dropdownId?: string;
}

const baseContainerStyle: React.CSSProperties = {
  width: "100%",
  minWidth: "240px",
  backgroundColor: colors.background.white,
  border: `1px solid ${colors.divider.default}`,
  borderRadius: "12px",
  boxShadow: "0px 12px 28px rgba(15, 23, 42, 0.12)",
  padding: "8px",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
};

export const SortDropdownContent = React.forwardRef<HTMLDivElement, SortDropdownContentProps>(
  function SortDropdownContent(
    { activeValue, options, onSelect, style, className = "", dropdownId }: SortDropdownContentProps,
    ref
  ) {
    return (
      <div
        ref={ref}
        id={dropdownId}
        data-testid="sort-dropdown"
        role="listbox"
        className={className}
        style={{
          ...baseContainerStyle,
          ...style,
        }}
      >
        {options.map((option) => {
          const isActive = option.value === activeValue;
          return (
            <button
              key={option.value}
              type="button"
              role="option"
              data-testid={`sort-option-${option.value}`}
              aria-selected={isActive}
              onClick={() => onSelect(option.value)}
              className="flex items-center justify-between transition-colors"
              style={{
                width: "100%",
                border: "none",
                backgroundColor: isActive ? colors.accent.veryLightBlueGray : "transparent",
                borderRadius: "10px",
                paddingLeft: "14px",
                paddingRight: "14px",
                paddingTop: "12px",
                paddingBottom: "12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span className="text-caption text-ink whitespace-nowrap font-medium">
                {option.label}
              </span>
              {isActive && (
                <span
                  aria-hidden="true"
                  style={{
                    width: "12px",
                    height: "9px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg
                    width="12"
                    height="9"
                    viewBox="0 0 12 9"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 4.5L4.5 8L11 1"
                      stroke={colors.text.black}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }
);

SortDropdownContent.displayName = "SortDropdownContent";
